"use client";

import { useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

interface GridGroupShellProps {
  glowColor: string;
  children: React.ReactNode;
}

/**
 * Wraps a group of consecutive grid sections with shared cursor tracking.
 * Renders a single cursor-following radial glow (continuous vertical lines
 * brightened where the cursor sits).
 *
 * Crosshair markers and horizontal cell borders are NOT this shell's job —
 * crosshairs are now drawn by `GridSectionCrosshairs` mounted inside each
 * section, and horizontal cell borders are drawn by per-cell CSS using
 * the `--grid-line-color` custom property.
 */
export function GridGroupShell({ glowColor, children }: GridGroupShellProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--mouse-x", "-999px");
    el.style.setProperty("--mouse-y", "-999px");
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {/* Cursor-following glow — vertical lines brightened under cursor */}
      <div
        className="absolute inset-0 z-25 pointer-events-none hidden lg:block"
        aria-hidden="true"
        style={{
          maskImage:
            "radial-gradient(circle 250px at var(--mouse-x, -999px) var(--mouse-y, -999px), black, transparent)",
          WebkitMaskImage:
            "radial-gradient(circle 250px at var(--mouse-x, -999px) var(--mouse-y, -999px), black, transparent)",
        }}
      >
        <div className="absolute inset-0 flex">
          <div className={cn("w-1/3 border-r", glowColor)} />
          <div className={cn("w-1/3 border-r", glowColor)} />
          <div className="w-1/3" />
        </div>
      </div>
    </div>
  );
}
